import type { FlowScreenConfig } from "@/shared/constants/flowTypes";

import { AdminFamilyTriageIntroScreen } from "@/pages/postloss/get-authorized/components/steps/AdminFamilyTriageIntroScreen";
import { AdminMarriagesScreen } from "@/pages/postloss/get-authorized/components/steps/AdminMarriagesScreen";
import { AdminChildrenScreen } from "@/pages/postloss/get-authorized/components/steps/AdminChildrenScreen";
import { AdminExtendedFamilyScreen } from "@/pages/postloss/get-authorized/components/steps/AdminExtendedFamilyScreen";
import { AdminReviewScreen } from "@/pages/postloss/get-authorized/components/steps/AdminReviewScreen";
import { AdminLoadingScreen } from "@/pages/postloss/get-authorized/components/steps/AdminLoadingScreen";
import type { AdminChildRow } from "@/pages/postloss/get-authorized/components/steps/adminFamilyTypes";

function validatePersonRows(
  rows: AdminChildRow[],
  prefix: string,
  errors: Record<string, string>,
  options: { requireMinor: boolean; requireHasChildren: boolean; recursiveHasChildren?: boolean },
) {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!row.firstName.trim()) errors[`${prefix}_${i}_first_name`] = "First name is required";
    if (!row.lastName.trim()) errors[`${prefix}_${i}_last_name`] = "Last name is required";
    if (options.requireMinor && !row.isMinor) errors[`${prefix}_${i}_is_minor`] = "This field is required";
    if (!row.status) errors[`${prefix}_${i}_status`] = "Status is required";
    if (row.status === "passed_away" && options.requireHasChildren) {
      if (!row.hasChildren) errors[`${prefix}_${i}_has_children`] = "This field is required";
      if (row.hasChildren === "yes") {
        validatePersonRows(row.children, `${prefix}_${i}_gc`, errors, {
          ...options,
          requireHasChildren: options.recursiveHasChildren ?? options.requireHasChildren,
        });
      }
    }
  }
}

function hasLivingDescendant(answers: Record<string, string | string[]>): boolean {
  const hasChildren = answers["admin_family_has_children"];
  if (hasChildren !== "yes") return false;
  try {
    const list = answers["admin_family_children_list"];
    if (typeof list !== "string") return false;
    const children = JSON.parse(list) as AdminChildRow[];
    const check = (rows: AdminChildRow[]): boolean => {
      for (const row of rows) {
        if (row.status === "living") return true;
        if (row.status === "passed_away" && row.hasChildren === "yes" && check(row.children)) return true;
      }
      return false;
    };
    return check(children);
  } catch {
    return false;
  }
}

export function createFamilyTriageScreens(): FlowScreenConfig[] {
  return [
    {
      id: "admin_family_triage_intro",
      layout: "full",
      customComponent: AdminFamilyTriageIntroScreen,
      getButtonText: () => "Get started",
    },
    {
      id: "admin_family_triage_marriages",
      layout: "full",
      customComponent: AdminMarriagesScreen,
      validate: (answers) => {
        const errors: Record<string, string> = {};
        const married = answers["admin_family_married_at_death"];

        if (!married) {
          errors.married_at_death = "Select an option to continue";
          return errors;
        }

        if (married === "yes") {
          if (!answers["admin_family_spouse_first_name"])
            errors.spouse_first_name = "First name is required";
          if (!answers["admin_family_spouse_last_name"])
            errors.spouse_last_name = "Last name is required";
          if (!answers["admin_family_spouse_status"])
            errors.spouse_status = "Status is required";
        }

        return errors;
      },
    },
    {
      id: "admin_family_triage_children",
      layout: "full",
      customComponent: AdminChildrenScreen,
      validate: (answers) => {
        const errors: Record<string, string> = {};
        const hasChildren = answers["admin_family_has_children"];

        if (!hasChildren) {
          errors.has_children = "Select an option to continue";
          return errors;
        }

        if (hasChildren === "yes") {
          try {
            const list = answers["admin_family_children_list"];
            if (typeof list === "string") {
              const children = JSON.parse(list) as AdminChildRow[];
              validatePersonRows(children, "child", errors, { requireMinor: true, requireHasChildren: true });
            }
          } catch {
            /* skip */
          }
        }

        return errors;
      },
    },
    {
      id: "admin_family_triage_extended",
      layout: "full",
      customComponent: AdminExtendedFamilyScreen,
      shouldDisplay: ({ answers }) => {
        const married = answers["admin_family_married_at_death"];
        const spouseStatus = answers["admin_family_spouse_status"];
        const hasLivingSpouse = married === "yes" && spouseStatus === "living";
        const hasDescendant = hasLivingDescendant(answers);

        if (hasLivingSpouse && !hasDescendant) return true;
        if (!hasLivingSpouse && !hasDescendant) return true;
        return false;
      },
      validate: (answers) => {
        const errors: Record<string, string> = {};
        const married = answers["admin_family_married_at_death"];
        const spouseStatus = answers["admin_family_spouse_status"];
        const hasLivingSpouse = married === "yes" && spouseStatus === "living";

        // Conditional parents mode: validate parent rows directly
        if (hasLivingSpouse) {
          try {
            const list = answers["admin_family_parents_list"];
            if (typeof list === "string") {
              const parents = JSON.parse(list) as AdminChildRow[];
              validatePersonRows(parents, "parent", errors, { requireMinor: false, requireHasChildren: false });
            }
          } catch { /* skip */ }
          return errors;
        }

        // Extended family mode
        const hasParents = answers["admin_family_has_living_parents"];
        if (!hasParents) {
          errors.has_living_parents = "Select an option to continue";
          return errors;
        }

        if (hasParents === "yes") {
          try {
            const list = answers["admin_family_parents_list"];
            if (typeof list === "string") {
              const parents = JSON.parse(list) as AdminChildRow[];
              validatePersonRows(parents, "parent", errors, { requireMinor: false, requireHasChildren: false });
            }
          } catch { /* skip */ }
          if (Object.keys(errors).length > 0) return errors;
        }

        // Siblings — validate when no living parent
        const noLivingParent = hasParents === "no_living_parents" ||
          (() => {
            if (hasParents !== "yes") return false;
            try {
              const list = answers["admin_family_parents_list"];
              if (typeof list !== "string") return false;
              const parents = JSON.parse(list) as AdminChildRow[];
              return parents.length > 0 && parents.every((p) => p.status === "passed_away");
            } catch { return false; }
          })();

        if (noLivingParent) {
          const hasSiblings = answers["admin_family_has_siblings"];
          if (!hasSiblings) {
            errors.has_siblings = "Select an option to continue";
            return errors;
          }
          if (hasSiblings === "no_siblings") {
            errors.dead_end = "This estate requires an attorney to proceed";
            return errors;
          }
          if (hasSiblings === "yes") {
            try {
              const list = answers["admin_family_siblings_list"];
              if (typeof list === "string") {
                const siblings = JSON.parse(list) as AdminChildRow[];
                validatePersonRows(siblings, "sibling", errors, { requireMinor: true, requireHasChildren: true, recursiveHasChildren: false });
              }
            } catch { /* skip */ }
          }
        }

        return errors;
      },
    },
    {
      id: "admin_family_triage_review",
      layout: "full",
      customComponent: AdminReviewScreen,
      getButtonText: () => "Confirm and save",
    },
    {
      id: "admin_family_triage_loading",
      layout: "full",
      hideButton: true,
      skipOnBack: true,
      customComponent: AdminLoadingScreen,
    },
  ];
}
